import React, { FC, useEffect } from 'react';
import styles from './CreateCampaignForm.module.scss';
import { Checkbox, Input, Select, TreeSelect } from 'esa-ui-library';
import { Form } from '../../../components';
import { useStore } from '../../../../common';
import { PropertyType } from 'esa-ui-library/lib/types';
import { validateForm } from '../FreeSpins.helper';

interface CreateCampaignFormProps {
	onValidityChange?: (isValid: boolean) => void;
}

const CreateCampaignForm: FC<CreateCampaignFormProps> = ({ onValidityChange }): JSX.Element => {
	const [state, dispatch] = useStore();
	// const { token } = useContext(AuthContext);
	const { campaignForm, formErrors, success, partners, activePartners, games, providers, currencies } = state;
	const statuses = [
		{ label: 'Disabled', value: 'disabled' },
		{ label: 'Enabled', value: 'enabled' },
	];
	const devices = [
		{ label: 'Desktop only', value: '0' },
		{ label: 'Mobile only', value: '1' },
		{ label: 'All devices', value: '9' },
	];

	const onChange = (field: string, val: string) => {
		dispatch('FORM', { field, value: val });
	};

	const onSelect = (field: string, val: PropertyType[] | boolean) => {
		dispatch('FORM', { field, value: val });
	};

	useEffect(() => {
		const errors = validateForm(campaignForm);
		onValidityChange?.(!errors);
	}, [campaignForm]);

	const onPartnersChange = (val: PropertyType[]) => {
		const leafNodes = val.filter((item: any) => !item.options || item.options.length === 0);

		const singleSelection = leafNodes.slice(-1);

		dispatch('PARTNERS_UPDATE', singleSelection);
		dispatch('FORM_CAMPAIGN_PARTNERS', { activePartners: singleSelection });

		onValidityChange?.(singleSelection.length > 0);
	};

	const providerOptions = [{ label: '— No provider —', value: '' }, ...providers];

	useEffect(() => {
		const providerValue = Array.isArray(campaignForm.provider_id)
			? campaignForm.provider_id[0]?.value
			: campaignForm.provider_id?.value || campaignForm.provider_id;

		if (providerValue && providerValue !== '') {
			dispatch('FORM', { field: 'all_games', value: false });
		} else {
			dispatch('FORM', { field: 'all_games', value: false });
		}
	}, [campaignForm.provider_id]);

	const getProviderValue = (provider: any) => {
		if (!provider) return null;
		if (Array.isArray(provider)) return provider[0]?.value ?? null;
		if (typeof provider === 'object') return provider.value ?? null;
		return provider;
	};

	const providerValue = getProviderValue(campaignForm.provider_id);

	const filteredGameOptions = games
		.filter((game: any) => {
			if (!providerValue) return true;

			if (campaignForm.all_games) return true;

			return game.game_provider_id?.toString() === providerValue.toString();
		})
		.map((game: any) => ({
			label: `${game.game_name} (${game.game_provider_name})`,
			value: game.internal_game_id,
		}));

	return (
		<section>
			<Form success={success}>
				<div className={styles.CreateCampaignForm}>
					<Input
						className={styles.Input}
						type='text'
						label='Campaign name'
						placeholder='Enter name'
						dataId='campaign-name-field'
						required
						onChange={(val) => onChange('campaign_name', val)}
						error={!!formErrors?.campaign_name}
						errorMessage={formErrors?.campaign_name}
					/>
					<Input
						className={styles.Input}
						type='text'
						label='Campaign Description'
						placeholder='Enter description'
						dataId='campaign-description-field'
						required
						onChange={(val) => onChange('campaign_description', val)}
						error={!!formErrors?.campaign_description}
						errorMessage={formErrors?.campaign_description}
					/>
					<Input
						className={styles.Input}
						type='text'
						label='Thirdparty campaign id'
						placeholder='Enter thirdparty campaign id'
						dataId='thirdparty-campaign-id-field'
						onChange={(val) => onChange('thirdparty_campaign_id', val)}
						error={!!formErrors?.thirdparty_campaign_id}
						errorMessage={formErrors?.thirdparty_campaign_id}
					/>
					<Select
						className={styles.Input}
						label='Current Status'
						options={statuses}
						required
						selected={campaignForm.status}
						placeholder='Select status'
						dataId='campaign-status-field'
						onChange={(val) => onSelect('status', val)}
						error={!!formErrors?.campaign_status}
						errorMessage={formErrors?.campaign_status}
					/>
					<TreeSelect
						className={styles.CustomTreeSelect}
						label='Partners'
						options={partners}
						placeholder='Select partners'
						selected={activePartners}
						required
						dataId='partners-field'
						onChange={onPartnersChange}
					/>
					<Input
						className={styles.Input}
						type='datetime-local'
						label='From'
						required
						placeholder='Select start datetime'
						dataId='from-datetime-field'
						onChange={(val) => onChange('from_date', val)}
						error={!!formErrors?.from_date}
						errorMessage={formErrors?.from_date}
					/>
					<Input
						className={styles.Input}
						type='datetime-local'
						label='To'
						required
						placeholder='Select end datetime'
						dataId='to-datetime-field'
						onChange={(val) => onChange('to_date', val)}
						error={!!formErrors?.to_date}
						errorMessage={formErrors?.to_date}
					/>
					<Select
						className={styles.Input}
						label='Device'
						options={devices}
						required
						selected={campaignForm.device}
						placeholder='Select status'
						dataId='campaign-status-field'
						onChange={(val) => onSelect('device', val)}
						error={!!formErrors?.device}
						errorMessage={formErrors?.device}
					/>
					{/*<Checkbox*/}
					{/*	className={styles.Checkbox}*/}
					{/*	checked={campaignForm.allow_api_award}*/}
					{/*	onChange={(val) => onSelect('allow_api_award', val)}*/}
					{/*	label='Allow api award'*/}
					{/*/>*/}
					<Checkbox
						className={styles.Checkbox}
						checked={campaignForm.auto_award_on_launch}
						onChange={(val) => onSelect('auto_award_on_launch', val)}
						label='Auto award on launch'
					/>
					<Checkbox
						className={styles.Checkbox}
						checked={campaignForm.all_users}
						onChange={(val) => onSelect('all_users', val)}
						label='All users'
					/>
					<Select
						className={styles.Input}
						label='Game provider'
						options={providerOptions}
						removeable
						required
						selected={campaignForm.provider_id}
						placeholder='Select provider'
						dataId='campaign-provider-field'
						onChange={(val) => onSelect('provider_id', val)}
						error={!!formErrors?.provider_id}
						errorMessage={formErrors?.provider_id}
					/>
					{!campaignForm.all_games && (
						<Select
							className={styles.Input}
							search={true}
							label='Games'
							removeable
							options={filteredGameOptions}
							multiple
							placeholder='Select games'
							selected={campaignForm.games}
							dataId='games-field'
							onChange={(val) => onSelect('games', val)}
						/>
					)}
					<Input
						className={styles.Input}
						type='text'
						label='Free spin total'
						placeholder='Enter name'
						dataId='freespin-total-value-field'
						required
						onChange={(val) => onChange('free_spin_total_value', val)}
						error={!!formErrors?.free_spin_total_value}
						errorMessage={formErrors?.free_spin_total_value}
					/>
					<Input
						className={styles.Input}
						type='text'
						label='Free spin bet'
						placeholder='Enter name'
						dataId='freespin-bet-value-field'
						required
						onChange={(val) => onChange('free_spin_bet_value', val)}
						error={!!formErrors?.free_spin_bet_value}
						errorMessage={formErrors?.free_spin_bet_value}
					/>
					<Select
						label='Currency'
						options={currencies}
						placeholder='Select currency'
						selected={campaignForm.free_spin_currency}
						dataId='currency-field'
						onChange={(val) => onSelect('free_spin_currency', val)}
						error={!!formErrors?.free_spin_currency}
						errorMessage={formErrors?.free_spin_currency}
					/>
					<Input
						className={styles.Input}
						type='datetime-local'
						label='Free spin expiry'
						required
						placeholder='Select start datetime'
						dataId='free-spin-expiry-from-datetime-field'
						onChange={(val) => onChange('from_spin_expiry', val)}
						error={!!formErrors?.from_spin_expiry}
						errorMessage={formErrors?.from_spin_expiry}
					/>
				</div>
			</Form>
		</section>
	);
};

export { CreateCampaignForm };
